<template>
    <Popover
        v-slot="slotProps"
        class="relative">
        <div
            class="leading-3"
            @mouseenter="handlePopoverMouseEnter(slotProps, $event)"
            @mouseleave="handlePopoverMouseLeave(slotProps, $event)">
            <PopoverButton
                ref="popoverTrigger"
                as="div"
                class="inline-flex items-center gap-x-1 text-sm cursor-pointer">
                <slot name="trigger" />
            </PopoverButton>

            <Transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 translate-y-1"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-1">
                <PopoverPanel
                    class="absolute left-1/2 z-10 mt-2 flex w-screen max-w-max transition-opacity"
                    :class="[position === 'center' ? 'px-4' : position === 'left' ? 'pl-4' : 'pr-4']"
                    :style="offset">
                    <div
                        ref="panel"
                        class="flex-auto rounded-xl bg-white px-4 py-2 text-sm overflow-hidden bg-white dark:bg-gray-800 text-sm leading-6 shadow-lg ring-1 dark:ring-gray-300 ring-gray-900/5"
                        :class="popoverStyles">
                        <slot />
                    </div>
                </PopoverPanel>
            </Transition>
        </div>
    </Popover>
</template>

<script setup>
const popoverTrigger = ref(null);

const props = defineProps({
    size: {
        type: String,
        default: 'md',
        description: 'large: lg || medium:md || small: sm'
    },
    position: {
        type: String,
        default: 'center',
        description: 'center || left || right'
    },
    trigger: {
        type: String,
        default: 'hover',
        description: 'hover || click'
    }
})

const panel = ref(null)

const panelBoundingRect = useElementBounding(panel)

const {width: windowWidth} = useWindowSize()

const offset = computed(() => {
    if (!panelBoundingRect) return ''

    const PADDING = 15
    // popover default positions
    const posX = panelBoundingRect.x.value
    const rightX = posX + panelBoundingRect.width.value

    const leftOffset = (posX < 0) ? Math.floor(posX) - PADDING : 0
    const rightOffset = (rightX > windowWidth.value) ? Math.floor(rightX - windowWidth.value) + PADDING : 0

    //position by props, tailwindcss cannot handle dynamic class bindings at runtime, so this has to be done manually
    if (props.position === 'center') return `transform: translateX(calc(-50% - ${leftOffset}px - ${rightOffset}px));`
    if (props.position === 'left') return `transform: translateX(calc(-100% - ${leftOffset}px - ${rightOffset}px));`
    if (props.position === 'right') return `transform: translateX(calc(${leftOffset}px - ${rightOffset}px));`
})

function handlePopoverMouseEnter({open}, event) {
    if (open || props.trigger === 'click' || event.sourceCapabilities.firesTouchEvents) return
    if (popoverTrigger.value) {
        popoverTrigger.value.$el.click()
    }
}

function handlePopoverMouseLeave({open, close}, event) {
    if (!open || props.trigger === 'click' || event.sourceCapabilities.firesTouchEvents) return
    close()
}

const popoverStyles = computed(() => {
    if (props.size === 'sm') {
        return 'max-w-sm'
    } else if (props.size === 'md') {
        return 'max-w-md'
    } else {
        return 'max-w-lg'
    }
})
</script>

<style scoped>

</style>